import DashboardService from "@/src/api/DashboardService.js";
import ViewsServiceV2 from "@/src/api/ViewsServiceV2.js";
import CRMService from "@/src/api/CRMService.js";
import { message } from "ant-design-vue";
import { formatDateTime } from "@/src/utils/utils.js";
import {
  ATTRIBUTE_TYPES,
  isFileLikeType,
  EXCLUDED_ATTRIBUTE_TYPES_IN_LIST_VIEWS,
} from "@/src/constants/crmConstants.js";
import { superAGIUser } from "@/src/constants/userConstants.js";

export const resolveStructuredOptionLabel = (value, structuredOptions) => {
  if (
    value == null ||
    !Array.isArray(structuredOptions) ||
    structuredOptions.length === 0
  ) {
    return null;
  }
  const strValue = String(value);
  const option = structuredOptions.find(
    (opt) => String(opt.id) === strValue || String(opt.value) === strValue,
  );
  return option?.label || null;
};

export const isUUID = (str) => {
  if (typeof str !== "string") return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    str,
  );
};

export const applyTypeSpecificHeaderProperties = (
  header,
  type,
  slug,
  attribute = {},
) => {
  switch (type) {
    case "domain":
      header.view = true;
      break;
    case "boolean":
      header.render = "boolean";
      break;
    case "tags":
      header.render = "tags";
      break;
    case "currency":
      header.render = "currency";
      break;
    case "date":
    case "datetime":
      header.render = "date";
      break;
    case "user":
      if (attribute.is_multi_value) {
        header.multiType = "avatarGroup";
      } else {
        header.view = true;
      }
      break;
    case "action":
      header.ellipsis = false;
      break;
  }

  if (
    slug &&
    (slug.includes("logo") || slug.includes("image") || slug.includes("avatar"))
  ) {
    header.avatar = slug;
  }

  if (slug === "created_at" || slug === "updated_at") {
    header.render = "date";
  }

  return header;
};

// Pre-defined system fields - created once, reused
const SYSTEM_FIELDS = {
  name: {
    label: "Name",
    key: "name",
    width: 2,
    avatar: "profile_pic",
    suffix: true,
  },
  score: { label: "Score", key: "score", width: 2 },
  owner: { label: "Owner", key: "owner", width: 2, view: true },
  last_activity: { label: "Last Activity", key: "last_activity", width: 2 },
  created_at: { label: "Created At", key: "created_at", width: 2 },
  updated_at: { label: "Updated At", key: "updated_at", width: 2 },
};
const SYSTEM_FIELD_KEYS = Object.keys(SYSTEM_FIELDS);

export const generateTableHeadersFromSchema = (
  attributes = [],
  options = {},
) => {
  // Pre-calculate excluded fields Set for O(1) lookup
  const excludeSet = options.excludeFields
    ? new Set(options.excludeFields)
    : null;

  const includeSystem = options.includeSystemFields !== false;

  // Pre-allocate array with estimated size
  const estimatedSize =
    (includeSystem ? SYSTEM_FIELD_KEYS.length : 0) + attributes.length;
  const headers = new Array(estimatedSize);
  let headerIndex = 0;

  // Add system fields if needed
  if (includeSystem) {
    for (const fieldKey of SYSTEM_FIELD_KEYS) {
      if (!excludeSet || !excludeSet.has(fieldKey)) {
        headers[headerIndex++] = SYSTEM_FIELDS[fieldKey];
      }
    }
  }

  // Create Set of system field keys for fast lookup
  const systemFieldSet = includeSystem ? new Set(SYSTEM_FIELD_KEYS) : null;

  // Process attributes - separate personal_name for ordering
  const personalNameFields = [];

  for (const attribute of attributes) {
    const { slug, name, type } = attribute;

    // Skip if excluded
    if (excludeSet && excludeSet.has(slug)) continue;
    // Skip if it's a system field and we're including system fields
    if (systemFieldSet && systemFieldSet.has(slug)) continue;
    // Skip attribute types that shouldn't render in list views (e.g. image —
    // still drives the avatar, but has no column/cell representation).
    if (EXCLUDED_ATTRIBUTE_TYPES_IN_LIST_VIEWS.includes(String(type || "").toLowerCase())) continue;

    const header = { label: name, key: slug, width: 2, attributeType: type };
    applyTypeSpecificHeaderProperties(header, type, slug, attribute);

    if (type === "personal_name") {
      personalNameFields.push(header);
    } else {
      headers[headerIndex++] = header;
    }
  }

  // Insert personal name fields before other custom fields
  // Trim array to actual size and prepend personal names after system fields
  const trimmedHeaders = headers.slice(0, headerIndex);

  if (personalNameFields.length > 0) {
    // Find where system fields end
    const systemEndIndex = includeSystem
      ? SYSTEM_FIELD_KEYS.filter((k) => !excludeSet || !excludeSet.has(k))
          .length
      : 0;

    // Insert personal name fields after system fields
    return [
      ...trimmedHeaders.slice(0, systemEndIndex),
      ...personalNameFields,
      ...trimmedHeaders.slice(systemEndIndex),
    ];
  }

  return trimmedHeaders;
};

export const saveColumnOrder = async (
  viewId,
  columnSlugs,
  schemaObjectData,
  apiVersion = "v1",
) => {
  try {
    if (!viewId) {
      console.error("No view ID available for saving column order");
      return { success: false, error: "No view ID" };
    }

    if (!columnSlugs || columnSlugs.length === 0) {
      console.error("No column order provided");
      return { success: false, error: "No columns provided" };
    }

    const slugToIdMap = {};
    if (schemaObjectData?.properties) {
      schemaObjectData.properties.forEach((prop) => {
        if (prop.id && prop.slug) {
          slugToIdMap[prop.slug] = prop.id;
        }
      });
    }

    const columnIds = columnSlugs
      .map((slug) => slugToIdMap[slug])
      .filter((id) => id !== undefined);

    if (columnIds.length === 0) {
      console.error("No valid column IDs found");
      return { success: false, error: "No valid column IDs" };
    }

    const payload = {
      columns: columnIds,
    };

    if (apiVersion === "v2") {
      await ViewsServiceV2.updateView(viewId, payload);
    } else {
      await DashboardService.applyFilter(viewId, payload);
    }

    return {
      success: true,
      slugs: columnSlugs,
      ids: columnIds,
    };
  } catch (error) {
    console.error("Error saving column order:", error);
    return { success: false, error: error.message };
  }
};

export const buildSchemaTypeMaps = (schemaObjectData) => {
  const schemaTypeMap = {};
  const propertyIdToSlugMap = {};
  const structuredOptionsMap = {};
  if (schemaObjectData?.properties) {
    for (const property of schemaObjectData.properties) {
      schemaTypeMap[property.slug] = property.datatype || property.type;
      if (property.id) {
        propertyIdToSlugMap[property.id] = property.slug;
      }
      if (property.structured_options?.length > 0) {
        structuredOptionsMap[property.slug] = property.structured_options;
      }
    }
  }
  return { schemaTypeMap, propertyIdToSlugMap, structuredOptionsMap };
};

export const buildSlugToIdMap = (schemaObjectData) => {
  const slugToIdMap = {};
  const idToSlugMap = {};

  if (schemaObjectData?.properties) {
    schemaObjectData.properties.forEach((prop) => {
      if (prop.id && prop.slug) {
        slugToIdMap[prop.slug] = prop.id;
        idToSlugMap[prop.id] = prop.slug;
      }
    });
  }

  return { slugToIdMap, idToSlugMap };
};

export const checkIfFirstTextProperty = async (
  objectSlug,
  propertyType,
  CRMService,
  excludedFormFields = [],
  service = null,
) => {
  if (propertyType !== "text") {
    return false;
  }

  try {
    const response = await CRMService.fetchSchemaObject(objectSlug, service);
    if (response.success) {
      const filteredAttributes = response?.schema?.attributes?.filter(
        (attr) => !excludedFormFields.includes(attr.slug),
      );

      const existingPrimary = filteredAttributes.find(
        (attr) => attr.is_primary === true,
      );
      if (existingPrimary) {
        return false;
      }

      const textProperties = filteredAttributes.filter(
        (attr) => attr.type === "text",
      );

      return textProperties.length === 0;
    }
    return false;
  } catch (error) {
    console.error("Error checking if first text property:", error);
    return false;
  }
};

export const setPropertyAsPrimary = async (
  objectSlug,
  attributeSlug,
  CRMService,
  attributeData = null,
  service = null,
) => {
  try {
    let fullAttribute = attributeData;

    if (!fullAttribute) {
      const attributeResponse = await CRMService.fetchSchemaAttribute(
        objectSlug,
        attributeSlug,
        service,
      );
      fullAttribute = attributeResponse?.attribute;
    }

    const payload = {
      ...fullAttribute,
      is_primary: true,
      is_required: true,
      is_unique: fullAttribute?.is_unique || false,
    };

    await CRMService.updateSchemaAttributeMetadata(
      objectSlug,
      attributeSlug,
      payload,
      service,
    );
    return { success: true };
  } catch (error) {
    console.error("Error setting property as primary:", error);
    return { success: false, error };
  }
};

export const addPropertyToActiveView = async (
  viewId,
  propertyName,
  schemaObjectData,
  currentColumns,
  DashboardService,
) => {
  try {
    if (!viewId || !schemaObjectData?.properties) {
      return { success: false, error: "Missing required data" };
    }

    const slugToIdMap = {};
    schemaObjectData.properties.forEach((prop) => {
      if (prop.id && prop.slug) {
        slugToIdMap[prop.slug] = prop.id;
      }
    });

    const existingColumnIds = currentColumns
      .map((slug) => slugToIdMap[slug])
      .filter((id) => id !== undefined);

    if (existingColumnIds.length === 0) {
      return { success: true, noFilterUpdate: true, message: "All columns visible, no filter needed" };
    }

    const newAttribute = schemaObjectData.properties.find(
      (prop) => prop.name === propertyName,
    );

    if (newAttribute?.id && !existingColumnIds.includes(newAttribute.id)) {
      existingColumnIds.push(newAttribute.id);

      await DashboardService.applyFilter(viewId, {
        columns: existingColumnIds,
      });

      return { success: true };
    }

    return { success: false, error: "Property not found or already exists" };
  } catch (error) {
    console.error("Error adding property to active view:", error);
    return { success: false, error };
  }
};

export const addNewAttributeToActiveView = async (
  entityType,
  attributeId,
  service = null,
) => {
  try {
    if (!entityType || !attributeId) {
      console.error(
        "[addNewAttributeToActiveView] Missing required parameters",
      );
      return { success: false, error: "Missing required parameters" };
    }

    const { standardObjects } =
      await import("@/src/constants/objectConstants.js");
    const standardObject = standardObjects.find(
      (obj) => obj.slug === entityType || obj.viewSlug === entityType,
    );
    const viewSlug = standardObject ? standardObject.viewSlug : entityType;

    const viewData = await CRMService.fetchDynamicEntityViews(
      viewSlug,
      null,
      service,
    );

    if (!viewData?.success || !viewData?.currentView?.id) {
      console.error("[addNewAttributeToActiveView] No active view found");
      return { success: false, error: "No active view found" };
    }

    const existingColumnIds = [...(viewData.currentView.columns || [])];
    
    if (existingColumnIds.length === 0) {
      return { success: true, message: "All columns visible, no filter needed" };
    }

    if (existingColumnIds.includes(attributeId)) {
      return { success: true, message: "Attribute already in view" };
    }

    existingColumnIds.push(attributeId);

    await DashboardService.applyFilter(viewData.currentView.id, {
      columns: existingColumnIds,
    });

    return { success: true };
  } catch (error) {
    console.error("[addNewAttributeToActiveView] Error:", error);
    return { success: false, error: error.message || "Unknown error" };
  }
};

export const extractPhoneNumber = (phoneData) => {
  if (!phoneData) return "";

  if (typeof phoneData === "string") {
    return phoneData;
  }

  return (
    phoneData?.normalized_phone_number ||
    phoneData?.normalizedphonenumber ||
    phoneData?.original_phone_number ||
    phoneData?.originalphonenumber ||
    ""
  );
};

export const reorderTableHeadersByColumns = (tableHeaders, columnSlugs) => {
  if (!columnSlugs || columnSlugs.length === 0) {
    return;
  }

  const addPropertyHeader = tableHeaders.value.find(
    (h) => h.key === "add_property",
  );
  const otherHeaders = tableHeaders.value.filter(
    (h) => h.key !== "add_property",
  );

  const headersMap = new Map(otherHeaders.map((h) => [h.key, h]));

  const orderedHeaders = columnSlugs
    .map((slug) => headersMap.get(slug))
    .filter(Boolean);

  const remainingHeaders = otherHeaders.filter(
    (h) => !columnSlugs.includes(h.key),
  );

  tableHeaders.value = [...orderedHeaders, ...remainingHeaders];

  if (addPropertyHeader) {
    tableHeaders.value.push(addPropertyHeader);
  }
};

export const applyFrozenColumnsToHeaders = (tableHeaders, frozenColumns) => {
  if (!frozenColumns.value?.length) {
    tableHeaders.value = tableHeaders.value.map((header) => {
      if (header.key === "add_property") {
        return header;
      }
      const { fixed, ...rest } = header;
      return rest;
    });
    return;
  }

  const frozenSet = new Set(frozenColumns.value);

  tableHeaders.value = tableHeaders.value.map((header) => {
    if (header.key === "add_property") {
      return header;
    }

    if (frozenSet.has(header.key)) {
      return { ...header, fixed: "left" };
    }

    const { fixed, ...rest } = header;
    return rest;
  });
};

export const transformRecordForTable = (
  record,
  schemaObjectData,
  users = [],
  workspaceCurrencies = [],
  prebuiltMaps = null,
) => {
  let lastActivity =
    record.attributes?.last_activity || record.last_activity || null;
  if (lastActivity) {
    if (typeof lastActivity === "string") {
      try {
        lastActivity = JSON.parse(lastActivity);
      } catch (e) {
        lastActivity = null;
      }
    } else if (lastActivity.value && typeof lastActivity.value === "string") {
      try {
        lastActivity = JSON.parse(lastActivity.value);
      } catch (e) {
        lastActivity = null;
      }
    }
  }

  const transformedRecord = {
    id: record.id,
    created_at: formatDateTime(record.created_at, "full"),
    updated_at: formatDateTime(record.updated_at, "full"),
    created_by: record.created_by,
    last_modified_by: record.last_modified_by,
    workspace_id: record.workspace_id,
    object_type: record.object_type,
    version: record.version,
    last_activity: lastActivity,
  };

  const phoneNumbersCollector = [];
  const emailsCollector = [];

  const collectEmails = (rawEmailArray) => {
    if (!Array.isArray(rawEmailArray)) return;
    rawEmailArray.forEach((item) => {
      const email =
        typeof item === "string"
          ? item
          : item?.email || item?.originalemailaddress || "";
      if (email) emailsCollector.push(email);
    });
  };

  const normalizePhoneObject = (phoneData, index, fieldName) => {
    if (!phoneData) return null;
    if (typeof phoneData === "string") {
      return {
        phone_number: phoneData,
        type: fieldName || "phone",
        id: `phone_${index}`,
      };
    }
    const number = extractPhoneNumber(phoneData);
    if (!number) return null;
    return {
      phone_number: number,
      type: phoneData.type || phoneData.phone_type || fieldName || "phone",
      id: phoneData.id || `phone_${index}`,
    };
  };

  const collectPhoneNumbers = (rawPhoneArray, fieldName) => {
    if (!Array.isArray(rawPhoneArray)) return;
    rawPhoneArray.forEach((item, idx) => {
      const normalized = normalizePhoneObject(
        item,
        phoneNumbersCollector.length + idx,
        fieldName,
      );
      if (normalized) phoneNumbersCollector.push(normalized);
    });
  };

  const ensureString = (value, key = null) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  let schemaTypeMap, propertyIdToSlugMap, structuredOptionsMap;
  if (prebuiltMaps) {
    schemaTypeMap = prebuiltMaps.schemaTypeMap;
    propertyIdToSlugMap = prebuiltMaps.propertyIdToSlugMap;
    structuredOptionsMap = prebuiltMaps.structuredOptionsMap || {};
  } else {
    const maps = buildSchemaTypeMaps(schemaObjectData);
    schemaTypeMap = maps.schemaTypeMap;
    propertyIdToSlugMap = maps.propertyIdToSlugMap;
    structuredOptionsMap = maps.structuredOptionsMap;
  }

  const isDateType = (type) => {
    if (!type) return false;
    const normalizedType = type.toLowerCase();
    return (
      normalizedType === ATTRIBUTE_TYPES.DATE ||
      normalizedType === ATTRIBUTE_TYPES.DATETIME
    );
  };

  const transformAttributeByType = (key, attribute, schemaType) => {
    if (Array.isArray(attribute.value)) {
      const arrValue = attribute.value;
      if (schemaType === ATTRIBUTE_TYPES.PHONE_NUMBER) {
        if (key === "phone_number") collectPhoneNumbers(arrValue, key);
        const phoneNumber = extractPhoneNumber(arrValue[0]);
        return ensureString(phoneNumber, key);
      }
      if (schemaType === ATTRIBUTE_TYPES.EMAIL) {
        if (key === "email") collectEmails(arrValue);
        const emails = arrValue
          .map((item) => (typeof item === "string" ? item : item?.email || ""))
          .filter((email) => email !== "");
        return ensureString(emails.join(", "), key);
      }
      if (schemaType === ATTRIBUTE_TYPES.DOMAIN) {
        const domains = arrValue
          .map((item) => (typeof item === "string" ? item : item?.domain || ""))
          .filter((domain) => domain !== "");
        return ensureString(domains.join(", "), key);
      }
      if (isFileLikeType(schemaType)) {
        return arrValue;
      }
      if (
        schemaType === ATTRIBUTE_TYPES.MULTI_SELECT ||
        schemaType === "tags"
      ) {
        return arrValue;
      }
      if (schemaType === ATTRIBUTE_TYPES.USER || schemaType === "user") {
        return arrValue;
      }
      const values = arrValue
        .map((item) =>
          typeof item === "object" ? JSON.stringify(item) : String(item),
        )
        .filter((val) => val !== "");
      return ensureString(values.join(", "), key);
    }

    if (
      typeof attribute.value === "string" &&
      attribute.value.trim().startsWith("[")
    ) {
      try {
        const parsedValue = JSON.parse(attribute.value);
        if (Array.isArray(parsedValue)) {
          if (
            schemaType === ATTRIBUTE_TYPES.EMAIL ||
            key.includes("email") ||
            key.includes("Email")
          ) {
            if (key === "email") collectEmails(parsedValue);
            const emails = parsedValue
              .map((item) => item.email || item.originalemailaddress || "")
              .filter((email) => email !== "");
            return ensureString(emails.join(", "), key);
          }
          if (schemaType === ATTRIBUTE_TYPES.DOMAIN || key.includes("domain")) {
            const domains = parsedValue
              .map((item) => item.domain || item.rootdomain || "")
              .filter((domain) => domain !== "");
            return ensureString(domains.join(", "), key);
          }
          if (
            schemaType === ATTRIBUTE_TYPES.PHONE_NUMBER ||
            key.includes("phone")
          ) {
            if (key === "phone_number") collectPhoneNumbers(parsedValue, key);
            const phoneNumber = extractPhoneNumber(parsedValue?.[0]);
            return ensureString(phoneNumber, key);
          }
          const values = parsedValue
            .map((item) =>
              typeof item === "object" ? JSON.stringify(item) : String(item),
            )
            .filter((val) => val !== "");
          return ensureString(values.join(", "), key);
        }
      } catch (e) {}
    }

    if (schemaType && isDateType(schemaType)) {
      let dateValue = attribute.value;

      if (
        typeof attribute.value === "string" &&
        attribute.value.trim().startsWith("{")
      ) {
        try {
          const parsed = JSON.parse(attribute.value);
          if (parsed.created_time) {
            dateValue = parsed.created_time;
          }
        } catch (e) {}
      } else if (
        typeof attribute.value === "object" &&
        attribute.value !== null
      ) {
        dateValue = attribute.value.created_time || null;
      }

      if (!dateValue) return "";

      if (schemaType.toLowerCase() === ATTRIBUTE_TYPES.DATE) {
        return formatDateTime(dateValue, "date");
      } else {
        return formatDateTime(dateValue, "full");
      }
    }

    if (schemaType === ATTRIBUTE_TYPES.BOOLEAN) {
      const value = attribute.value;
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      if (typeof value === "string") {
        const lowerValue = value.toLowerCase();
        if (lowerValue === "true") return "Yes";
        if (lowerValue === "false") return "No";
      }
    }

    // Resolve ID-type fields to their label using structured_options
    if (structuredOptionsMap[key]) {
      const label = resolveStructuredOptionLabel(
        attribute.value,
        structuredOptionsMap[key],
      );
      if (label) return label;
    }

    // Currency value (shape: { currency_code, currency_value }), possibly arriving
    // wrapped in a formula/property_sync { output_type, value } envelope. Keep the
    // object shape so CurrencyCell can format with the record's own currency_code.
    if (
      schemaType === "currency" &&
      attribute.value &&
      typeof attribute.value === "object"
    ) {
      const inner = attribute.value;
      const code = inner.currency_code ?? inner.currencycode;
      const val = inner.currency_value ?? inner.currencyvalue;
      if (val !== undefined || code !== undefined) {
        return { currency_code: code ?? "", currency_value: val ?? "" };
      }
    }

    return ensureString(attribute.value, key);
  };

  const resolveCurrencySymbol = (code) => {
    if (code && workspaceCurrencies && workspaceCurrencies.length > 0) {
      const match = workspaceCurrencies.find((c) => c.currency_code === code);
      if (match?.currency_symbol) return match.currency_symbol;
    }
    const primary = workspaceCurrencies?.find((c) => c.is_primary);
    return primary?.currency_symbol || "$";
  };

  const transformArrayAttributeByType = (key, attribute, schemaType) => {
    if (attribute.length === 0) {
      // Return empty array for file/image types, empty string for others
      return isFileLikeType(schemaType) ? [] : "";
    }
    switch (schemaType) {
      case ATTRIBUTE_TYPES.FILE:
      case ATTRIBUTE_TYPES.IMAGE:
        // Return files array as-is for file/image properties
        return attribute;

      case ATTRIBUTE_TYPES.EMAIL:
        if (key === "email") collectEmails(attribute);
        const emails = attribute
          .map((item) => item.email || item.originalemailaddress || "")
          .filter((email) => email !== "");
        return ensureString(emails.join(", "), key);

      case ATTRIBUTE_TYPES.DOMAIN:
        const domains = attribute
          .map((item) => item.domain || item.rootdomain || "")
          .filter((domain) => domain !== "");
        return ensureString(domains.join(", "), key);

      case ATTRIBUTE_TYPES.PHONE_NUMBER:
        if (key === "phone_number") collectPhoneNumbers(attribute, key);
        const phoneNumber = extractPhoneNumber(attribute?.[0]);
        return ensureString(phoneNumber, key);

      default:
        const values = attribute
          .map((item) =>
            typeof item === "object" ? JSON.stringify(item) : String(item),
          )
          .filter((val) => val !== "");
        return ensureString(values.join(", "), key);
    }
  };

  if (record.attributes) {
    Object.keys(record.attributes).forEach((key) => {
      // Skip fields that are already set from the outer record object
      if (
        key === "last_activity" ||
        key === "updated_at" ||
        key === "created_at"
      )
        return;

      const attribute = record.attributes[key];
      let schemaType = schemaTypeMap[key];

      if (
        schemaType === ATTRIBUTE_TYPES.PROPERTY_SYNC &&
        attribute?.source_type
      ) {
        schemaType = attribute.source_type.toLowerCase();
      } else if (
        schemaType === ATTRIBUTE_TYPES.FORMULA &&
        attribute?.output_type
      ) {
        schemaType = attribute.output_type.toLowerCase();
      }

      if (attribute && typeof attribute === "object") {
        if (attribute.value !== undefined) {
          let effectiveType = schemaType;
          if (
            schemaTypeMap[key] === ATTRIBUTE_TYPES.PROPERTY_SYNC &&
            attribute.source_type
          ) {
            effectiveType = attribute.source_type.toLowerCase();
          } else if (
            schemaTypeMap[key] === ATTRIBUTE_TYPES.FORMULA &&
            attribute.output_type
          ) {
            effectiveType = attribute.output_type.toLowerCase();
          }
          transformedRecord[key] = transformAttributeByType(
            key,
            attribute,
            effectiveType,
          );
        } else if (Array.isArray(attribute)) {
          transformedRecord[key] = transformArrayAttributeByType(
            key,
            attribute,
            schemaType,
          );
        } else if (
          schemaType === "currency" &&
          (attribute.currency_value !== undefined ||
            attribute.currencyvalue !== undefined)
        ) {
          // Keep the object shape so CurrencyCell formats with the record's own
          // currency_code rather than a workspace-default fallback.
          transformedRecord[key] = {
            currency_code:
              attribute.currency_code ?? attribute.currencycode ?? "",
            currency_value:
              attribute.currency_value ?? attribute.currencyvalue ?? "",
          };
        } else if (isFileLikeType(schemaType) && attribute.files) {
          // Extract files array from {files: [...]} wrapper (file and image types)
          transformedRecord[key] = Array.isArray(attribute.files)
            ? attribute.files
            : [];
        } else if (schemaType === ATTRIBUTE_TYPES.SELECT_TIMESTAMP) {
          if (attribute.value && typeof attribute.value === "object") {
            transformedRecord[key] = {
              currentvalue: attribute.value.currentvalue,
              timestampvalue: attribute.value.timestampvalue,
            };
          } else {
            transformedRecord[key] = attribute;
          }
        } else if (schemaType === ATTRIBUTE_TYPES.RECORD && attribute.records) {
          // Preserve records array for record type properties
          transformedRecord[key] = attribute;
        } else {
          // Check if this is a datetime field
          if (schemaType && isDateType(schemaType)) {
            let dateValue = attribute;

            // Check if it's a stringified JSON object
            if (
              typeof attribute === "string" &&
              attribute.trim().startsWith("{")
            ) {
              try {
                const parsed = JSON.parse(attribute);
                if (parsed.created_time) {
                  dateValue = parsed.created_time;
                }
              } catch (e) {
                // If parsing fails, use the original value
              }
            } else if (typeof attribute === "object" && attribute !== null) {
              dateValue = attribute.created_time || null;
            }

            if (dateValue) {
              transformedRecord[key] = formatDateTime(
                dateValue,
                schemaType.toLowerCase() === ATTRIBUTE_TYPES.DATE
                  ? "date"
                  : "full",
              );
            } else {
              transformedRecord[key] = "";
            }
          } else {
            transformedRecord[key] = ensureString(
              JSON.stringify(attribute),
              key,
            );
          }
        }
      } else {
        // Handle raw datetime values
        if (schemaType && isDateType(schemaType)) {
          let dateValue = attribute;

          // Check if it's a stringified JSON object
          if (
            typeof attribute === "string" &&
            attribute.trim().startsWith("{")
          ) {
            try {
              const parsed = JSON.parse(attribute);
              if (parsed.created_time) {
                dateValue = parsed.created_time;
              }
            } catch (e) {
              // If parsing fails, use the original value
            }
          } else if (typeof attribute === "object" && attribute !== null) {
            dateValue = attribute.created_time || null;
          }

          if (dateValue) {
            transformedRecord[key] = formatDateTime(
              dateValue,
              schemaType.toLowerCase() === ATTRIBUTE_TYPES.DATE
                ? "date"
                : "full",
            );
          } else {
            transformedRecord[key] = "";
          }
        } else {
          transformedRecord[key] = ensureString(attribute, key);
        }
      }
    });
  }

  const userTypeFields = [];
  if (schemaObjectData?.properties) {
    schemaObjectData.properties.forEach((property) => {
      const isUserType =
        property.datatype === ATTRIBUTE_TYPES.USER || property.type === "user";

      const fieldValue = transformedRecord[property.slug];
      if (isUserType && (fieldValue || fieldValue === 0)) {
        userTypeFields.push({
          slug: property.slug,
          isMultiValue: property.is_multi_value || false,
        });
      }
    });
  }

  const amountSlug = propertyIdToSlugMap["amount"] || "amount";
  const currencyCodeSlug =
    propertyIdToSlugMap["currency_code"] || "currency_code";

  let currencyCode = record[currencyCodeSlug];
  if (!currencyCode && record.attributes?.[currencyCodeSlug]) {
    const currencyCodeAttr = record.attributes[currencyCodeSlug];
    currencyCode = currencyCodeAttr?.value || currencyCodeAttr;
  }

  const getCurrencySymbol = (code) => {
    if (!code || !workspaceCurrencies || workspaceCurrencies.length === 0) {
      const primaryCurrency = workspaceCurrencies?.find(
        (curr) => curr.is_primary,
      );
      return primaryCurrency?.currency_symbol || "$";
    }
    const currency = workspaceCurrencies.find(
      (curr) => curr.currency_code === code,
    );
    return currency?.currency_symbol || "$";
  };

  if (transformedRecord[amountSlug] && transformedRecord[amountSlug] !== "") {
    const currencySymbol = getCurrencySymbol(currencyCode);
    const amount = transformedRecord[amountSlug];

    if (
      currencySymbol &&
      !String(amount).startsWith(currencySymbol) &&
      !String(amount).includes(currencySymbol)
    ) {
      transformedRecord[amountSlug] = `${currencySymbol}${amount}`;
    }
  }

  const findUserById = (userId, usersList) => {
    if (userId == superAGIUser.id) {
      return superAGIUser;
    }
    if (userId == 0) {
      return { id: 0, name: "SuperAGI" };
    }
    return usersList.find((u) => u.id == userId);
  };

  userTypeFields.forEach((fieldInfo) => {
    const fieldKey = fieldInfo.slug;
    const userValue = transformedRecord[fieldKey];
    const deletedFlagKey = `is_${fieldKey}_deleted`;

    // Initialize deleted flag as false
    transformedRecord[deletedFlagKey] = false;

    if (users.length > 0 || userValue == superAGIUser.id || userValue == 0) {
      if (Array.isArray(userValue)) {
        const userResults = userValue.map((userId) => {
          const user = findUserById(userId, users);
          return {
            userId,
            user,
            found: user !== undefined,
            isDeleted: user?.isDeleted || false,
          };
        });

        const foundUsers = userResults
          .filter((r) => r.found)
          .map((r) => r.user);
        const hasDeletedUsers = userResults.some(
          (r) => (!r.found && r.userId) || r.isDeleted,
        );

        if (fieldInfo.isMultiValue) {
          // For multi-value, include found users and mark if any were deleted
          transformedRecord[fieldKey] = foundUsers;
          transformedRecord[deletedFlagKey] = hasDeletedUsers;
        } else {
          const user = foundUsers[0];
          if (user) {
            if (user.isDeleted) {
              transformedRecord[deletedFlagKey] = true;
            }
            transformedRecord[fieldKey] = user.name || user.email || "";
            transformedRecord.logo_url =
              user.logo_url || user.profile_pic || null;
            transformedRecord.profile_pic = user.profile_pic || null;
            transformedRecord.avatar_color = user.avatar_color || null;
          } else if (hasDeletedUsers) {
            // User ID exists but user not found - deleted user
            transformedRecord[fieldKey] = `User ${userValue[0]}`;
            transformedRecord[deletedFlagKey] = true;
          } else {
            transformedRecord[fieldKey] = "";
          }
        }
      } else if (userValue) {
        const user = findUserById(userValue, users);
        if (user) {
          if (user.isDeleted) {
            transformedRecord[deletedFlagKey] = true;
          }
          if (fieldInfo.isMultiValue) {
            transformedRecord[fieldKey] = [user];
          } else {
            transformedRecord[fieldKey] = user.name || user.email || "";
            transformedRecord.logo_url =
              user.logo_url || user.profile_pic || null;
            transformedRecord.profile_pic = user.profile_pic || null;
            transformedRecord.avatar_color = user.avatar_color || null;
          }
        } else {
          // User ID exists but user not found - this is a deleted user
          transformedRecord[deletedFlagKey] = true;
          if (fieldInfo.isMultiValue) {
            transformedRecord[fieldKey] = [];
          } else {
            // Keep a placeholder showing it was a deleted user
            transformedRecord[fieldKey] = `User ${userValue}`;
          }
        }
      }
    } else if (userValue) {
      // No users available to look up - if there's a value, assume deleted
      transformedRecord[deletedFlagKey] = true;
      if (Array.isArray(userValue)) {
        transformedRecord[fieldKey] = fieldInfo.isMultiValue ? [] : "";
      } else {
        transformedRecord[fieldKey] = `User ${userValue}`;
      }
    }
  });

  // Preserve top-level phone arrays from the original record (standard contacts/leads)
  if (record.phone_numbers && !transformedRecord.phone_numbers) {
    transformedRecord.phone_numbers = record.phone_numbers;
  }
  if (
    record.company_phone_numbers &&
    !transformedRecord.company_phone_numbers
  ) {
    transformedRecord.company_phone_numbers = record.company_phone_numbers;
  }

  // Populate phone_numbers from collected phone_number type attributes (custom objects)
  if (phoneNumbersCollector.length > 0) {
    if (!transformedRecord.phone_numbers) {
      transformedRecord.phone_numbers = phoneNumbersCollector;
    }
    if (!transformedRecord.phone_number) {
      transformedRecord.phone_number =
        phoneNumbersCollector[0]?.phone_number || "";
    }
  }

  // Populate email from collected email type attributes (for EmailCompose)
  if (emailsCollector.length > 0) {
    if (!transformedRecord.email) {
      transformedRecord.email = emailsCollector[0];
    }
  }

  return transformedRecord;
};

export const handlePropertyAddedCommon = async ({
  property,
  schemaObjectData,
  tableHeaders,
  records,
  columns,
  views,
  viewFilterRef,
  onBuildFieldMapping,
  showSuccessMessage = true,
}) => {
  try {
    if (property && property.id && property.slug) {
      const newProperty = {
        id: property.id,
        slug: property.slug,
        name: property.name,
        datatype: property.type,
        description: property.description || "",
        required: property.is_required || false,
        is_primary: property.is_primary || false,
        is_required: property.is_required || false,
        is_system: property.is_system || false,
        options: property.options || [],
        default_value: property.default_value,
      };

      schemaObjectData.value.properties.push(newProperty);

      const config = { width: 2 };
      const newHeader = {
        label: property.name,
        key: property.slug,
        width: config.width,
        ...config,
      };

      applyTypeSpecificHeaderProperties(
        newHeader,
        property.type,
        property.slug,
      );

      const addPropertyHeaderIndex = tableHeaders.value.findIndex(
        (h) => h.key === "add_property",
      );

      if (addPropertyHeaderIndex > -1) {
        tableHeaders.value.splice(addPropertyHeaderIndex, 0, newHeader);
      } else {
        tableHeaders.value.push(newHeader);
      }

      if (onBuildFieldMapping) {
        onBuildFieldMapping(schemaObjectData.value.properties);
      }

      records.value.forEach((record) => {
        if (!(property.slug in record)) {
          record[property.slug] = "";
        }
      });

      if (viewFilterRef.value?.fetchDynamicFilterOptions) {
        await viewFilterRef.value.fetchDynamicFilterOptions();
      }

      if (views.value.current?.id && schemaObjectData.value && property?.name) {
        const result = await addPropertyToActiveView(
          views.value.current.id,
          property.name,
          schemaObjectData.value,
          columns.value,
          DashboardService,
        );

        if (result.success && !result.noFilterUpdate) {
          if (!columns.value.includes(property.slug)) {
            columns.value.push(property.slug);
          }

          if (views.value.current.columns) {
            if (!views.value.current.columns.includes(property.slug)) {
              views.value.current.columns.push(property.slug);
            }
          }
        } else if (!result.success) {
          message.warning(
            "Column added locally but failed to update view on server. Please refresh the page.",
          );
        }
      }

      if (showSuccessMessage) {
        message.success("Property added successfully");
      }

      return { success: true };
    }

    return { success: false, error: "Invalid property data" };
  } catch (error) {
    console.error("Error in handlePropertyAddedCommon:", error);
    message.error("Failed to add property");
    return { success: false, error };
  }
};

export const convertViewColumnsToSlugs = (viewColumns, schemaObjectData) => {
  if (!viewColumns || !viewColumns.length || !schemaObjectData?.properties) {
    return [];
  }

  const idToSlugMap = {};
  schemaObjectData.properties.forEach((prop) => {
    if (prop.id && prop.slug) {
      idToSlugMap[prop.id] = prop.slug;
    }
  });

  const slugs = viewColumns
    .map((col) => {
      if (typeof col === "string" && col !== "id") {
        const slug = idToSlugMap[col];
        if (slug) {
          return slug;
        }
        const existsInSchema = schemaObjectData.properties.find(
          (p) => p.slug === col,
        );
        if (existsInSchema) {
          return col;
        }
        return null;
      }
      return null;
    })
    .filter((col) => col !== null && col !== undefined);

  return slugs;
};

export const buildStandardBulkPayload = ({
  views,
  selection,
  searchQuery,
  additionalData = {},
}) => {
  const viewId = views.value?.current?.id || 1;
  const payload = {
    view_id: viewId,
    select_all: selection.value?.select_all || false,
    unselected_ids: selection.value?.unselected_ids || [],
    selected_ids: selection.value?.selected_ids || [],
    ...additionalData,
  };
  const query = searchQuery?.value ?? searchQuery;
  if (query && query.trim() !== "") {
    payload.search_query = query;
  }
  return payload;
};

export const validateViewId = ({
  views,
  message,
  operation = "bulk operation",
}) => {
  const viewId = views.value?.current?.id || 1;
  if (!viewId) {
    message.error(`View ID is required for ${operation}`);
    return null;
  }
  return viewId;
};

export const validateRecordIds = ({
  selection,
  message,
  operation = "operation",
}) => {
  const recordIds = selection.value?.selected_ids || [];
  if (!recordIds.length) {
    message.warning(`Please select items to ${operation}`);
    return null;
  }
  return recordIds;
};

export const handleBulkOperationError = ({
  error,
  message,
  operation = "operation",
}) => {
  console.error(`Error in bulk ${operation}:`, error);
  const errorMessage =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    `An error occurred while ${operation} records`;
  message.error(errorMessage);
};

export const handleBulkOperationResponse = ({
  response,
  message,
  cancelBulkOperation,
  operation = "operation",
  loadingState,
}) => {
  if (!response?.success) {
    message.error(
      response?.message || response?.error || `Failed to ${operation} records`,
    );
    if (loadingState) {
      loadingState.value = false;
    }
    if (cancelBulkOperation) {
      cancelBulkOperation();
    }
    return false;
  }
  return true;
};

export const deduplicateRelationships = (relationships = []) => {
  if (!relationships || relationships.length === 0) {
    return [];
  }

  const relationshipsByKey = new Map();
  relationships.forEach((rel) => {
    const targetSlug = rel.target_object?.slug || rel.target_object_id;
    const sourceSlug = rel.source_object?.slug || rel.source_object_id;
    const cardinality = rel.cardinality || "unknown";
    // Include target service so two relationships pointing at same-slug objects
    // across different services (e.g. the CRM and products-service Products)
    // aren't merged into one row.
    const targetService = rel.target_object?.service || "";
    const key = `${sourceSlug}__${targetSlug}__${targetService}__${cardinality}`;

    if (!relationshipsByKey.has(key)) {
      relationshipsByKey.set(key, []);
    }
    relationshipsByKey.get(key).push(rel);
  });

  const dedupedRelationships = [];
  relationshipsByKey.forEach((rels) => {
    if (rels.length > 1) {
      const oldRel = rels.find((r) => r.target_object === null);
      const newRel = rels.find((r) => r.target_object !== null);

      if (
        oldRel &&
        newRel &&
        oldRel.target_object_id === newRel.target_object?.slug
      ) {
        dedupedRelationships.push(newRel);
      } else {
        dedupedRelationships.push(rels[0]);
      }
    } else {
      dedupedRelationships.push(rels[0]);
    }
  });

  return dedupedRelationships;
};

export const applyColumnWidthsToHeaders = (tableHeaders, widthsBySlug) => {
  if (!tableHeaders?.value) return;
  const map = widthsBySlug instanceof Map
    ? widthsBySlug
    : new Map(Object.entries(widthsBySlug || {}));

  tableHeaders.value = tableHeaders.value.map((header) => {
    if (!header || header.key === "add_property") return header;
    const persisted = map.get(header.key);
    if (typeof persisted === "number" && persisted > 0) {
      return { ...header, width: persisted };
    }
    if (header.width !== undefined) {
      const { width, ...rest } = header;
      return rest;
    }
    return header;
  });
};

export const saveColumnWidths = async (
  viewId,
  widthsBySlug,
  schemaObjectData,
  apiVersion = "v1",
) => {
  try {
    if (!viewId) {
      console.error("No view ID available for saving column widths");
      return { success: false, error: "No view ID" };
    }

    if (!widthsBySlug || Object.keys(widthsBySlug).length === 0) {
      return { success: false, error: "No widths provided" };
    }

    const slugToIdMap = {};
    if (schemaObjectData?.properties) {
      schemaObjectData.properties.forEach((prop) => {
        if (prop.id && prop.slug) {
          slugToIdMap[prop.slug] = prop.id;
        }
      });
    }

    const widthsById = {};
    for (const [slug, width] of Object.entries(widthsBySlug)) {
      const id = slugToIdMap[slug];
      if (id !== undefined && typeof width === "number") {
        widthsById[String(id)] = Math.round(width);
      }
    }

    if (Object.keys(widthsById).length === 0) {
      return { success: false, error: "No valid column IDs" };
    }

    const payload = { column_widths: widthsById };

    if (apiVersion === "v2") {
      await ViewsServiceV2.updateView(viewId, payload);
    } else {
      await DashboardService.applyFilter(viewId, payload);
    }

    return { success: true, widths: widthsById };
  } catch (error) {
    console.error("Error saving column widths:", error);
    return { success: false, error: error.message };
  }
};
